<template>
  <!-- Every colour and the optional background image come from the token layer
       (useThemeTokens.js): chrome / cta / surface tokens + --bg-image-header. -->
  <header ref="headerRoot" class="header">
    <nav class="nav-container" :class="{ 'is-offline': offline }">
      <!-- Desktop Navigation (hidden while the site is offline: the sections do not exist) -->
      <div v-if="!offline" class="nav-links desktop-nav">
        <a href="#home" class="nav-link">{{ $t('nav.home') }}</a>
        <a href="#services" class="nav-link">{{ $t('nav.massages') }}</a>
        <a href="#atmosphere" class="nav-link">{{ $t('nav.atmosphere') }}</a>
        <a href="#location" class="nav-link">{{ $t('nav.visitUs') }}</a>
        <a href="#faq" class="nav-link">{{ $t('nav.faq') }}</a>
      </div>
      
      <!-- Desktop actions: Book now + language selector -->
      <div class="header-actions">
        <button
          v-if="!offline"
          type="button"
          class="header-cta"
          @pointerdown="prefetchBookingSheet"
          @click="openBookingSheet({ source: 'header' })"
        >
          {{ $t('services.bookNow') }}
        </button>

        <div class="language-selector desktop-lang">
          <div class="dropdown">
            <button
              ref="langBtn"
              type="button"
              class="dropdown-btn"
              :aria-label="$t('common.language')"
              aria-haspopup="menu"
              :aria-expanded="showLangDropdown ? 'true' : 'false'"
              @click="toggleLangDropdown"
            >
              {{ currentLocale.toUpperCase() }}
              <span class="dropdown-arrow" :class="{ rotated: showLangDropdown }" aria-hidden="true">▼</span>
            </button>
            <div v-if="showLangDropdown" class="dropdown-menu" role="menu">
              <button
                v-for="lang in languages"
                :key="lang.code"
                type="button"
                role="menuitem"
                @click="changeLanguage(lang.code)"
                :class="['dropdown-item', { active: currentLocale === lang.code }]"
              >
                {{ lang.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile: language picker only (navigation handled by the sticky CTA bar / BottomNav) -->
      <div class="mobile-menu">
        <button
          ref="mobileLangBtn"
          type="button"
          class="mobile-lang-toggle"
          @click="toggleMobileMenu"
          :aria-label="`${$t('common.language')}: ${currentLocale.toUpperCase()}`"
          aria-haspopup="menu"
          :aria-expanded="showMobileMenu ? 'true' : 'false'"
        >
          <span class="globe-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
          </span>
          <span class="mobile-locale-label">{{ currentLocale.toUpperCase() }}</span>
          <span class="dropdown-arrow" :class="{ rotated: showMobileMenu }" aria-hidden="true">▼</span>
        </button>

        <!-- Language menu -->
        <div v-if="showMobileMenu" class="mobile-dropdown lang-only" role="menu">
          <button
            v-for="lang in languages"
            :key="lang.code"
            type="button"
            role="menuitem"
            @click="changeLanguage(lang.code)"
            :class="['mobile-lang-item', { active: currentLocale === lang.code }]"
          >
            {{ lang.label }}
          </button>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useBookingSheet } from '@/composables/useBookingSheet'

export default {
  name: 'AppHeader',
  props: {
    // Site offline (App.vue): no in-page nav and no Book Now, language picker stays.
    offline: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { locale } = useI18n()
    const { open: openBookingSheet, prefetch: prefetchBookingSheet } = useBookingSheet()
    const showLangDropdown = ref(false)
    const showMobileMenu = ref(false)
    const showMobileLangDropdown = ref(false)
    const headerRoot = ref(null)
    const langBtn = ref(null)
    const mobileLangBtn = ref(null)

    const languages = [
      { code: 'en', label: 'English' },
      { code: 'de', label: 'Deutsch' },
      { code: 'it', label: 'Italiano' },
      { code: 'fr', label: 'Français' },
      { code: 'sk', label: 'Slovensky' },
      { code: 'pt', label: 'Português' },
      { code: 'hu', label: 'Magyar' }
    ]

    const currentLocale = computed(() => locale.value)

    const changeLanguage = (lang) => {
      locale.value = lang
      showLangDropdown.value = false
      showMobileLangDropdown.value = false
      showMobileMenu.value = false
    }

    const toggleLangDropdown = () => {
      showLangDropdown.value = !showLangDropdown.value
    }

    const toggleMobileMenu = () => {
      showMobileMenu.value = !showMobileMenu.value
      if (showMobileMenu.value) {
        showMobileLangDropdown.value = false
      }
    }

    const toggleMobileLangDropdown = () => {
      showMobileLangDropdown.value = !showMobileLangDropdown.value
    }

    const closeMobileMenu = () => {
      showMobileMenu.value = false
      showMobileLangDropdown.value = false
    }

    const closeAllMenus = () => {
      showLangDropdown.value = false
      closeMobileMenu()
    }

    // Close on a click anywhere outside the header
    const onDocumentClick = (event) => {
      if (!showLangDropdown.value && !showMobileMenu.value) return
      if (headerRoot.value && headerRoot.value.contains(event.target)) return
      closeAllMenus()
    }

    // Close on Escape and return focus to the toggle that opened the menu
    const onDocumentKeydown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return
      if (!showLangDropdown.value && !showMobileMenu.value) return
      const focusTarget = showLangDropdown.value ? langBtn.value : mobileLangBtn.value
      closeAllMenus()
      focusTarget?.focus?.()
    }

    onMounted(() => {
      document.addEventListener('click', onDocumentClick)
      document.addEventListener('keydown', onDocumentKeydown)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocumentClick)
      document.removeEventListener('keydown', onDocumentKeydown)
    })

    return {
      headerRoot,
      langBtn,
      mobileLangBtn,
      openBookingSheet,
      prefetchBookingSheet,
      languages,
      currentLocale,
      changeLanguage,
      showLangDropdown,
      showMobileMenu,
      showMobileLangDropdown,
      toggleLangDropdown,
      toggleMobileMenu,
      toggleMobileLangDropdown,
      closeMobileMenu
    }
  }
}
</script>

<style scoped>
.header {
  background-image: var(--bg-image-header, none), var(--gradient-chrome);
  background-size: cover, cover;
  background-position: center, center;
  background-repeat: no-repeat, no-repeat;
  background-blend-mode: overlay;
  padding: 0 var(--gutter);
  /* Downward offset composed from the colour-only token (Footer.vue does the
     same with its upward offset), so both bars share one shadow colour. */
  box-shadow: 0 4px 12px var(--chrome-shadow-color);
  position: sticky;
  top: var(--banner-h, 0);
  z-index: 1000;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  min-height: 56px;
}

@media (min-width: 769px) {
  .nav-container {
    min-height: 64px;
  }
}

/* Offline: only the language picker is left, keep it on the right. */
.nav-container.is-offline {
  justify-content: flex-end;
}

/* Desktop actions (Book now + language) */
.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-cta {
  min-height: 44px;
  padding: 0 1.25rem;
  border: none;
  border-radius: 50px;
  background: var(--gradient-cta);
  color: var(--text-on-cta);
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: var(--shadow-brand);
  transition: transform 0.2s ease;
}

.header-cta:hover {
  transform: translateY(-1px);
}

.header-cta:active {
  transform: translateY(0) scale(0.98);
}

.header-cta:focus-visible,
.dropdown-btn:focus-visible,
.dropdown-item:focus-visible,
.mobile-lang-toggle:focus-visible,
.mobile-lang-item:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

@media (prefers-reduced-motion: reduce) {
  .header-cta,
  .header-cta:hover,
  .header-cta:active {
    transform: none;
    transition: none;
  }
}

/* Desktop Navigation */
.desktop-nav {
  display: flex;
  gap: 2rem;
}

.nav-link {
  color: var(--chrome-link);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  padding: 0.5rem 0;
  cursor: pointer;
}

.nav-link:hover {
  color: var(--chrome-link-hover);
}

/* Desktop Language Selector */
.desktop-lang {
  display: flex;
}

.dropdown {
  position: relative;
}

.dropdown-btn {
  background-color: transparent;
  border: 2px solid var(--chrome-link);
  color: var(--chrome-link);
  min-height: 44px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  border-radius: 4px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dropdown-btn:hover {
  background-color: var(--surface-hover);
}

.dropdown-arrow {
  transition: transform 0.3s ease;
  font-size: 0.8rem;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--surface-card);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 1000;
  margin-top: 0.5rem;
  overflow: hidden;
}

.dropdown-item {
  width: 100%;
  min-height: 44px;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  color: var(--text-on-card);
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 0.95rem;
}

.dropdown-item:hover {
  background-color: var(--surface-hover);
}

.dropdown-item.active {
  background: var(--color-accent);
  color: var(--text-primary);
}

/* Mobile Menu */
.mobile-menu {
  display: none;
  position: relative;
}

/* Language toggle button */
.mobile-lang-toggle {
  display: flex;
  align-items: center;
  min-height: 44px;
  gap: 0.35rem;
  background: transparent;
  border: 1.5px solid var(--chrome-link);
  color: var(--chrome-link);
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.2s ease;
}

.mobile-lang-toggle:hover {
  background-color: var(--surface-hover);
}

.globe-icon {
  display: flex;
  align-items: center;
}

.mobile-locale-label {
  letter-spacing: 0.05em;
}

.mobile-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: var(--surface-card);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  min-width: 140px;
  z-index: 1001;
}

.mobile-lang-item {
  width: 100%;
  min-height: 44px;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  color: var(--text-on-card);
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease;
  font-size: 0.9rem;
  font-weight: 500;
  display: block;
}

.mobile-lang-item:hover {
  background-color: var(--surface-hover);
}

.mobile-lang-item.active {
  background: var(--color-accent);
  color: var(--text-primary);
  font-weight: 700;
}

/* Responsive */
@media (max-width: 768px) {
  .desktop-nav,
  .desktop-lang,
  .header-actions {
    display: none;
  }

  .mobile-menu {
    display: block;
  }

  .nav-container {
    justify-content: flex-end;
  }
}
</style>
